/**
 * TRENCH RUN — cartridge palette.
 *
 * Sunset over an olive-drab desert trench. Player hovercraft is the
 * cool-toned hero against the warm desert wash. Three enemy classes get
 * distinct accent colors so the player can read threat at a glance:
 * red Swooper (sine-wave swarmer), orange Mine (floor turret), magenta
 * Hunter (homing fast-mover).
 *
 * Heritage palette: Defender's red/cyan/magenta-on-black plus
 * Choplifter's tan/olive ground.
 */

// Sky — sunset gradient top→horizon. The cartridge renders this as a
// linearGradient at runtime; tokens here are the gradient stops.
export const SKY_TOP = '#1a0830';
export const SKY_MID = '#7a2010';
export const SKY_HORIZON = '#d04018';

// Trench floor — olive desert, two tones for slope shading.
export const FLOOR = '#6a6018';
export const FLOOR_DARK = '#3a3408';
export const FLOOR_LINE = '#a09028'; // floor surface highlight

// Background silhouette ridgeline — distant mountains, black against sky.
export const RIDGE_FAR = '#1a0a18';
export const RIDGE_NEAR = '#0a0408';

// Base (delivery pad) — a lit pad at world x=0. Cyan-cool to match the
// player so it reads as "friendly home."
export const BASE_PAD = '#3fb8e6';
export const BASE_PAD_DIM = '#0c4e6a';
export const BASE_LIGHT = '#ffea00';

// Player hovercraft — cyan + cool, the cartridge hero.
export const PLAYER_BODY = '#3fb8e6';
export const PLAYER_OUTLINE = '#0c4e6a';
export const PLAYER_COCKPIT = '#7af3c8';
export const PLAYER_THRUST = '#ffb030';

// Civilians — bright sunset orange so they pop against the olive
// floor. Wave-arm animation cycles a lighter accent.
export const CIVILIAN_BODY = '#ff8c1a';
export const CIVILIAN_HEAD = '#ffdba8';
export const CIVILIAN_WAVE = '#ffea00';

// Enemy: Swooper — red glider on sine wave. Classic Defender lander.
export const SWOOPER_BODY = '#e63a3a';
export const SWOOPER_DARK = '#7a1010';
export const SWOOPER_EYE = '#ffea00';

// Enemy: Mine — orange floor turret. Sits, lobs arcs upward.
export const MINE_BODY = '#ff9020';
export const MINE_DARK = '#a04a08';
export const MINE_BARREL = '#5a2a04';

// Enemy: Hunter — magenta homing fast-mover. Defender mutant heritage.
export const HUNTER_BODY = '#d83ad8';
export const HUNTER_DARK = '#7a1a7a';
export const HUNTER_TRAIL = 'rgba(216, 58, 216, 0.4)';

// Mine arc-shot — slow rising fireball.
export const MINE_SHOT = '#ffea00';
export const MINE_SHOT_TRAIL = 'rgba(255, 234, 0, 0.5)';

// Player bullets — bright cyan, distinct from any enemy color.
export const BULLET = '#7af3c8';
export const BULLET_TRAIL = 'rgba(122, 243, 200, 0.5)';

// HUD typography.
export const HUD_LABEL = '#7c8089';
export const HUD_SCORE = '#ffea00';
export const HUD_LIVES = '#3fb8e6';
export const HUD_LEVEL = '#ff9020';
export const HUD_CARRY = '#ff8c1a';
export const HUD_ALERT = '#ff3030';

// Radar (top strip) — dark band with bright cyan dots over olive
// terrain silhouette.
export const RADAR_BG = '#0c0c14';
export const RADAR_BORDER = '#3a3408';
export const RADAR_PLAYER = '#3fb8e6';
export const RADAR_CIVILIAN = '#ff8c1a';
export const RADAR_ENEMY = '#e63a3a';
export const RADAR_BASE = '#ffea00';

// Effects.
export const FLASH_HIT = '#ffffff';

/** Tokens the cabinet samples for marquee tinting while this cartridge
 *  is loaded. First entry is the cartridge's hero color. */
export const PALETTE_HINT: readonly string[] = [
  SKY_HORIZON, // sunset orange — the cartridge's iconic color
  PLAYER_BODY,
  CIVILIAN_BODY,
  FLOOR,
];
